import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  UrlSegmentGroup,
  UrlSerializer,
} from '@angular/router';

import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PageHeadingFocusService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly urlSerializer = inject(UrlSerializer);
  private previousPath: string | null = null;

  /** Moves programmatic focus to the newly rendered page heading after navigation. */
  public start(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        const path = this.primaryPathOf(event.urlAfterRedirects);

        if (path === this.previousPath) {
          return;
        }

        this.previousPath = path;
        setTimeout(() => this.focusPageHeading());
      });
  }

  /**
   * The primary-outlet path only, with every auxiliary outlet segment removed.
   *
   * Sidecars are auxiliary routes, so opening and closing one changes the URL —
   * `/clerk-review/:id` becomes `/clerk-review/:id(document-history:history/1)` — while the
   * page itself does not. Comparing the raw URL treats both as a new page, and the heading
   * this then focuses is the visually hidden `<h1>` at the top of the scroll container, so
   * closing a sidecar scrolled the user back to the top of the page (#173433,
   * a regression from the focus handling added in #172307).
   *
   * Walks the primary-outlet chain rather than stripping parentheses from the string.
   * Angular nests auxiliary groups when a sidecar route has a child outlet of its own
   * (`serializeSegment` emits `path/(outlet:…)` recursively), and a regex cannot count
   * parentheses — it would strip the inner group, leave the outer one behind, and quietly
   * stop matching. Query params and the fragment are parsed off into the UrlTree, so they
   * are excluded here too, as before.
   */
  private primaryPathOf(url: string): string {
    const parts: string[] = [];
    let group: UrlSegmentGroup | undefined = this.urlSerializer.parse(url).root;

    while (group) {
      parts.push(...group.segments.map(segment => segment.path));
      group = group.children[PRIMARY_OUTLET];
    }

    return `/${parts.join('/')}`;
  }

  private focusPageHeading(): void {
    // Sidecars are routes, so opening one emits NavigationEnd like any page change — but
    // its `cdkTrapFocusAutoCapture` has already put focus inside the panel by the time this
    // runs. Moving focus to the heading drops the user behind the open panel, where Tab
    // walks the page instead of the dialog, which is the outcome #172053 exists to prevent.
    if (this.document.activeElement?.closest('[cdkTrapFocus]')) {
      return;
    }

    const heading =
      this.document.querySelector<HTMLElement>('#main-content h1');

    if (!heading) {
      return;
    }

    heading.tabIndex = -1;
    heading.focus();
  }
}
